/**
 * PlotCarousel — a compact, presentational carousel for paging through a small set of
 * "topic" charts one at a time (used in the report card for a monadic open-end, where the
 * grouped theme bar would otherwise stack many topics into a tall column). Renders one slide
 * at a time with prev/next arrows and dot reference points. Pure + render-only: all slides
 * arrive as ReactNodes; no data fetching or plot logic lives here.
 */
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { ChevronLeft, ChevronRight } from '@digital-twins/design-system/icons'

import { cn } from '@/lib/utils'

export interface CarouselSlide {
  key: string
  node: ReactNode
  // Human label for the dot's aria-label / tooltip (e.g. the theme name).
  label?: string
}

export interface PlotCarouselProps {
  slides: Array<CarouselSlide>
  // Describes the carousel for assistive tech (e.g. "Message takeout themes").
  ariaLabel?: string
  className?: string
}

export function PlotCarousel({ slides, ariaLabel, className }: PlotCarouselProps) {
  const { t } = useTranslation('app')
  const [index, setIndex] = useState(0)
  const count = slides.length

  if (count === 0) {
    return null
  }

  const clamped = Math.min(index, count - 1)
  const current = slides[clamped]
  const go = (next: number) => setIndex(((next % count) + count) % count)

  return (
    <div
      role="group"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      className={cn('space-y-3', className)}
      data-testid="qs-plot-carousel"
    >
      <div
        aria-live="polite"
        data-testid="qs-plot-carousel-slide"
        data-slide-index={clamped}
        data-slide-key={current.key}
      >
        {current.node}
      </div>

      {count > 1 && (
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => go(clamped - 1)}
            aria-label={t('quickStudyChrome.previousChart')}
            data-testid="qs-plot-carousel-prev"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-twin-chat-divider text-twin-chat-fg-muted transition-colors hover:bg-twin-chat-bubble/40 hover:text-twin-chat-fg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-twin-chat-divider"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div
            role="tablist"
            aria-label={t('quickStudyChrome.chartSelector')}
            className="flex flex-wrap items-center justify-center gap-1.5"
          >
            {slides.map((s, i) => {
              const active = i === clamped
              return (
                <button
                  key={s.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-label={s.label ? `Chart ${i + 1} of ${count}: ${s.label}` : `Chart ${i + 1} of ${count}`}
                  title={s.label}
                  onClick={() => go(i)}
                  data-testid="qs-plot-carousel-dot"
                  data-active={active ? 'true' : 'false'}
                  // Small visual dot, but a larger padded hit target for pointer + touch a11y.
                  className="group/dot flex items-center justify-center p-1.5 focus-visible:outline-none"
                >
                  <span
                    className={cn(
                      'h-1.5 rounded-full transition-all group-focus-visible/dot:ring-1 group-focus-visible/dot:ring-twin-chat-divider',
                      active ? 'w-4 bg-twin-chat-fg' : 'w-1.5 bg-twin-chat-fg/25 group-hover/dot:bg-twin-chat-fg/45',
                    )}
                  />
                </button>
              )
            })}
          </div>

          <button
            type="button"
            onClick={() => go(clamped + 1)}
            aria-label={t('quickStudyChrome.nextChart')}
            data-testid="qs-plot-carousel-next"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-twin-chat-divider text-twin-chat-fg-muted transition-colors hover:bg-twin-chat-bubble/40 hover:text-twin-chat-fg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-twin-chat-divider"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}

export default PlotCarousel
